import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import {
  Loader2, Calendar, MapPin, Flame, Sparkles, Music, Wine, Sun, Moon,
  PartyPopper, ChevronRight, ChevronDown, Mail, Building2, Ticket, Compass, Heart,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Legacy supplier marketplace categories that used to live at /:city/:category
const SUPPLIER_CATS = ['food', 'drinks', 'entertainment', 'staffing', 'logistics', 'decor', 'photography', 'food_truck'];

const VIBE_ICONS = {
  'nightlife': Moon, 'live-music': Music, 'food-drink': Wine,
  'day-parties': Sun, '21-plus': PartyPopper, 'outdoor': Compass,
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T12:00:00');
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtPrice = (p) => (p === 0 ? 'Free' : p ? `from $${p}` : '');

function EventCard({ e, compact = false, saved, onToggleSave }) {
  const tidSuffix = compact ? `trending-${e.slug}` : e.slug;
  return (
    <Link to={e.url} data-testid={`hub-event-${tidSuffix}`}
      className={`group block rounded-2xl overflow-hidden border border-border bg-card hover:border-fuchsia-500/50 transition-all ${compact ? 'w-[240px] flex-shrink-0' : ''}`}>
      <div className={`relative overflow-hidden bg-muted ${compact ? 'aspect-[4/5]' : 'aspect-[4/3]'}`}>
        {e.image_url
          ? <img src={e.image_url} alt={e.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Ticket className="h-8 w-8" /></div>}
        <button onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onToggleSave(e.event_id); }}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 backdrop-blur-md hover:bg-black/70 transition-colors"
          aria-label={saved ? 'Remove from saved' : 'Save event'}
          data-testid={`heart-${tidSuffix}`}>
          <Heart className={`h-4 w-4 ${saved ? 'fill-rose-500 text-rose-500' : 'text-white'}`} />
        </button>
        {e.badge && (
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-black/60 backdrop-blur-md text-amber-300 border border-amber-400/40">
            <Flame className="h-3 w-3 inline mr-0.5 -mt-0.5" />{e.badge}
          </span>
        )}
        {e.price_min != null && (
          <span className="absolute bottom-2 right-2 text-[11px] font-semibold px-2 py-1 rounded-full bg-black/60 backdrop-blur-md text-white">
            {fmtPrice(e.price_min)}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="font-semibold text-sm leading-snug line-clamp-2">{e.name}</p>
        <p className="text-xs text-muted-foreground mt-1">{fmtDate(e.event_date)}{e.event_time ? ` · ${e.event_time}` : ''}</p>
        {e.venue_name && <p className="text-xs text-fuchsia-300 truncate mt-0.5">{e.venue_name}</p>}
      </div>
    </Link>
  );
}

function WeekendDropBanner({ city, cityName }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/city-hub/${city}/weekend-drop`, { email });
      setDone(true);
      toast.success("You're in — first drop lands Thursday");
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not sign you up'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 backdrop-blur-xl p-6 sm:p-8" data-testid="weekend-drop-banner">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="text-lg font-bold flex items-center gap-2"><Mail className="h-5 w-5 text-indigo-400" /> Get the {cityName} Weekend Drop</p>
          <p className="text-sm text-muted-foreground mt-1">The 5 best events in the city, emailed to you every Thursday. No spam, just plans.</p>
        </div>
        {done ? (
          <p className="text-sm font-semibold text-emerald-400" data-testid="weekend-drop-done">✓ You're on the list</p>
        ) : (
          <form onSubmit={submit} className="flex gap-2 w-full sm:w-auto">
            <Input type="email" required placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)}
              className="sm:w-56" data-testid="weekend-drop-email" />
            <Button type="submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="weekend-drop-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign up'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function FaqAccordion({ faq }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2" data-testid="city-faq">
      {faq.map((f, i) => (
        <div key={i} className="rounded-xl border border-border overflow-hidden">
          <button onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between text-left px-4 py-3 text-sm font-semibold hover:bg-muted/40"
            data-testid={`faq-q-${i}`}>
            {f.q}
            <ChevronDown className={`h-4 w-4 transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>
          {open === i && <p className="px-4 pb-4 text-sm text-muted-foreground">{f.a}</p>}
        </div>
      ))}
    </div>
  );
}

export default function CityHubPage() {
  const { city, category: filter } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    const token = localStorage.getItem('eventism_token');
    if (!token) return;
    axios.get(`${API}/saved-events`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSavedIds(new Set(r.data.event_ids || [])))
      .catch(() => {});
  }, []);

  const toggleSave = useCallback(async (eventId) => {
    const token = localStorage.getItem('eventism_token');
    if (!token) {
      toast('Sign in to save events and get a reminder before they sell out');
      navigate(`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    try {
      const r = await axios.post(`${API}/saved-events/${eventId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSavedIds(prev => {
        const next = new Set(prev);
        if (r.data.saved) next.add(eventId); else next.delete(eventId);
        return next;
      });
      if (r.data.saved) toast.success("Saved — we'll remind you before it sells out");
      else toast('Removed from your saved events');
    } catch { toast.error('Could not save event'); }
  }, [navigate]);

  const isSupplierCat = filter && SUPPLIER_CATS.includes(filter);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/city-hub/${city}`, { params: { filter: filter || undefined, date: date || undefined } });
      setData(r.data);
    } catch (e) { setError(e.response?.status === 404 ? 'not-found' : 'error'); }
  }, [city, filter, date]);

  useEffect(() => { if (!isSupplierCat) load(); window.scrollTo(0, 0); }, [load, isSupplierCat]);

  // Legacy supplier URLs (/denver/food) now live under /suppliers
  if (isSupplierCat) return <Navigate to={`/suppliers/${city}/${filter}`} replace />;

  if (error === 'not-found') {
    return (
      <div className="min-h-screen bg-background pt-32 text-center px-4" data-testid="city-hub-empty">
        <Compass className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-2xl font-bold">Nothing here yet</h1>
        <p className="text-sm text-muted-foreground mt-2">No upcoming events found for this page.</p>
        <Button className="mt-5" onClick={() => navigate('/find-events')}>Browse all events</Button>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-background pt-32 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const { city_name, quick_counts: qc } = data;
  const pills = [
    { slug: 'tonight', label: 'Tonight', count: qc.tonight },
    { slug: 'this-weekend', label: 'This Weekend', count: qc.this_weekend },
    { slug: 'free', label: 'Free', count: qc.free },
    { slug: 'under-25', label: 'Under $25', count: qc.under_25 },
  ];
  const go = (slug) => navigate(slug === filter ? `/${city}` : `/${city}/${slug}`);

  const eventsLD = {
    '@context': 'https://schema.org', '@type': 'ItemList',
    itemListElement: data.events.slice(0, 20).map((e, i) => ({
      '@type': 'ListItem', position: i + 1,
      item: {
        '@type': 'Event', name: e.name, startDate: e.event_date,
        url: `${window.location.origin}${e.url}`, image: e.image_url || undefined,
        location: e.venue_name ? { '@type': 'Place', name: e.venue_name, address: city_name } : undefined,
        offers: e.price_min != null ? { '@type': 'Offer', price: e.price_min, priceCurrency: 'USD' } : undefined,
      },
    })),
  };

  const firstBatch = data.events.slice(0, 10);
  const restBatch = data.events.slice(10);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="city-hub-page">
      <SEO title={data.filter.meta_title} description={data.filter.meta_description} structuredData={[eventsLD]} />

      {/* Hero / command bar */}
      <header className="max-w-6xl mx-auto px-4 pt-8 pb-6">
        <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-fuchsia-400 mb-2">Eventism · {city_name}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-tight" data-testid="city-hub-h1">
          {data.filter.h1}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">{data.total_upcoming} upcoming events · updated daily</p>
        <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="quick-filter-bar">
          {pills.map(p => (
            <button key={p.slug} onClick={() => go(p.slug)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${filter === p.slug
                ? 'bg-fuchsia-600 text-white border-fuchsia-600'
                : 'border-border text-foreground hover:border-fuchsia-500/60'}`}
              data-testid={`pill-${p.slug}`}>
              {p.label}{p.count > 0 && <span className="ml-1.5 text-xs opacity-70">{p.count}</span>}
            </button>
          ))}
          <label className="relative inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border border-border hover:border-fuchsia-500/60 cursor-pointer">
            <Calendar className="h-4 w-4 mr-1.5" /> {date ? fmtDate(date) : 'Pick a date'}
            <input type="date" value={date} className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={e => setSearchParams(e.target.value ? { date: e.target.value } : {})}
              data-testid="date-picker" />
          </label>
          {(filter || date) && (
            <button onClick={() => navigate(`/${city}`)} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="clear-filters">
              Clear
            </button>
          )}
        </div>
      </header>

      {/* Trending carousel */}
      {!filter && !date && data.trending.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 mb-10" data-testid="trending-rail">
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2 mb-3">
            <Flame className="h-5 w-5 text-amber-400" /> Trending in {city_name}
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
            {data.trending.map(e => <div key={e.event_id} className="snap-start"><EventCard e={e} compact saved={savedIds.has(e.event_id)} onToggleSave={toggleSave} /></div>)}
          </div>
        </section>
      )}

      {/* Vibe pills */}
      <section className="max-w-6xl mx-auto px-4 mb-10" data-testid="vibe-pills">
        <h2 className="text-base md:text-lg font-bold flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-fuchsia-400" /> Browse by vibe
        </h2>
        <div className="flex flex-wrap gap-2">
          {data.vibes.map(v => {
            const Icon = VIBE_ICONS[v.slug] || Sparkles;
            return (
              <button key={v.slug} onClick={() => go(v.slug)} disabled={v.count === 0}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${filter === v.slug
                  ? 'bg-fuchsia-600 text-white border-fuchsia-600'
                  : v.count === 0 ? 'border-border/50 text-muted-foreground/50'
                    : 'border-border hover:border-fuchsia-500/60'}`}
                data-testid={`vibe-${v.slug}`}>
                <Icon className="h-4 w-4" /> {v.label}
                {v.count > 0 && <span className="text-xs opacity-70">{v.count}</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Event grid — first batch */}
      <section className="max-w-6xl mx-auto px-4 mb-10" data-testid="event-grid">
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2">
            <Ticket className="h-5 w-5 text-indigo-400" /> {data.filter.label ? `${data.filter.label} (${data.total_filtered})` : "What's on"}
          </h2>
          <Link to={`/events/${city}`} className="text-xs text-indigo-300 hover:underline flex items-center">
            All events <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        {firstBatch.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-2xl" data-testid="no-events-msg">
            Nothing matches this filter yet — try another vibe or clear the filters.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {firstBatch.map(e => <EventCard key={e.event_id} e={e} saved={savedIds.has(e.event_id)} onToggleSave={toggleSave} />)}
          </div>
        )}
      </section>

      {/* Mid-page micro-conversion */}
      <section className="max-w-6xl mx-auto px-4 mb-10">
        <WeekendDropBanner city={city} cityName={city_name} />
      </section>

      {restBatch.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 mb-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {restBatch.map(e => <EventCard key={e.event_id} e={e} saved={savedIds.has(e.event_id)} onToggleSave={toggleSave} />)}
          </div>
        </section>
      )}

      {/* Neighborhoods + top venues */}
      {(data.neighborhoods.length > 0 || data.top_venues.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 mb-10" data-testid="local-scene">
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-emerald-400" /> How {city_name} goes out
          </h2>
          {data.neighborhoods.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {data.neighborhoods.map(n => (
                <Link key={n.name} to={`/${city}/venues?neighborhood=${encodeURIComponent(n.name)}`}
                  className="px-4 py-2 rounded-full text-sm border border-border hover:border-emerald-500/60 transition-colors"
                  data-testid={`hood-${n.name.replace(/\s/g, '-').toLowerCase()}`}>
                  {n.name} <span className="text-xs text-muted-foreground">{n.count}</span>
                </Link>
              ))}
            </div>
          )}
          {data.top_venues.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data.top_venues.map(v => (
                <Link key={v.slug} to={v.url}
                  className="rounded-xl border border-border p-4 hover:border-fuchsia-500/50 transition-colors group"
                  data-testid={`top-venue-${v.slug}`}>
                  <Building2 className="h-5 w-5 text-fuchsia-400 mb-2" />
                  <p className="text-sm font-semibold group-hover:text-fuchsia-300">{v.name}</p>
                  <p className="text-xs text-muted-foreground">{v.count} upcoming {v.count === 1 ? 'event' : 'events'}</p>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* B2B callout */}
      <section className="max-w-6xl mx-auto px-4 mb-10">
        <div className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="b2b-callout">
          <p className="text-sm flex-1"><strong>Hosting an event in {city_name}?</strong>{' '}
            <span className="text-muted-foreground">Get in front of thousands of local eventgoers — publish on Eventism in minutes.</span></p>
          <Button size="sm" variant="outline" className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10"
            onClick={() => navigate('/partner-hub')} data-testid="b2b-cta">
            Become a partner
          </Button>
        </div>
      </section>

      {/* SEO copy + FAQ */}
      <section className="max-w-6xl mx-auto px-4 space-y-6">
        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl" data-testid="seo-copy">{data.seo_copy}</p>
        <FaqAccordion faq={data.faq} />
        <p className="text-xs text-muted-foreground">
          Planning an event? Browse {city_name}'s <Link to={`/suppliers/${city}`} className="text-indigo-300 hover:underline">supplier marketplace</Link> —
          caterers, DJs, photographers and more.
        </p>
      </section>
    </div>
  );
}
